// Actions
const UPDATE_DOC_SUBS = "hermes/docSubscriptions/UPDATE_DOC_SUBS";
const APPLY_SUB_EVENT = "hermes/docSubscriptions/APPLY_SUB_EVENT";
const RESUBSCRIBE = "hermes/docSubscriptions/RESUBSCRIBE";

let refreshSequence = 0;

const getRefreshTime = () => `${Date.now()}-${(refreshSequence += 1)}`;
const PRESENCE_KEYS = ["document", "serviceRequest"];

const initialState = {
  subList: [],
  subInstances: {},
  refreshTime: getRefreshTime(),
};

const hasValue = (value) =>
  value !== undefined && value !== null && String(value) !== "";

const getEntityKey = (value) => String(value);

const hasSubscriptionId = (subscriptionId) => hasValue(subscriptionId);

const getSubscriptionInstanceKey = (subscriptionId) => `id:${subscriptionId}`;

const hasActiveSubscriptions = (subscriptions = {}) =>
  Object.values(subscriptions).some((count) => count > 0);

const addSubscriptionInstance = (
  subInstances,
  key,
  id,
  userId,
  subscriptionId
) => {
  const targetId = getEntityKey(id);
  const userKey = getEntityKey(userId);
  const instanceKey = getSubscriptionInstanceKey(subscriptionId);
  const targetMap = subInstances[key] || {};
  const targetEntry = targetMap[targetId] || { id, users: {} };
  const userEntry = targetEntry.users?.[userKey] || {
    userId,
    subscriptions: {},
  };

  return {
    ...subInstances,
    [key]: {
      ...targetMap,
      [targetId]: {
        ...targetEntry,
        users: {
          ...(targetEntry.users || {}),
          [userKey]: {
            userId,
            subscriptions: {
              ...(userEntry.subscriptions || {}),
              [instanceKey]: 1,
            },
          },
        },
      },
    },
  };
};

const removeEmptyTarget = (subInstances, key, targetMap) => {
  const nextInstances = { ...subInstances };

  if (Object.keys(targetMap).length) {
    nextInstances[key] = targetMap;
  } else {
    delete nextInstances[key];
  }

  return nextInstances;
};

const removeSubscriptionInstance = (
  subInstances,
  key,
  id,
  userId,
  subscriptionId
) => {
  const targetId = getEntityKey(id);
  const userKey = getEntityKey(userId);
  const instanceKey = getSubscriptionInstanceKey(subscriptionId);
  const targetMap = subInstances[key] || {};
  const targetEntry = targetMap[targetId];
  const userEntry = targetEntry?.users?.[userKey];
  const hasCurrentSubscription = hasValue(
    userEntry?.subscriptions?.[instanceKey]
  );

  if (!hasCurrentSubscription) return subInstances;

  const nextSubscriptions = { ...userEntry.subscriptions };
  delete nextSubscriptions[instanceKey];

  const nextUsers = { ...targetEntry.users };

  if (hasActiveSubscriptions(nextSubscriptions)) {
    nextUsers[userKey] = {
      ...userEntry,
      subscriptions: nextSubscriptions,
    };
  } else {
    delete nextUsers[userKey];
  }

  const nextTargetMap = { ...targetMap };

  if (Object.keys(nextUsers).length) {
    nextTargetMap[targetId] = {
      ...targetEntry,
      users: nextUsers,
    };
  } else {
    delete nextTargetMap[targetId];
  }

  return removeEmptyTarget(subInstances, key, nextTargetMap);
};

const getSubListEntry = (key, targetEntry) => {
  const users = Object.values(targetEntry.users || {})
    .filter((userEntry) => hasActiveSubscriptions(userEntry.subscriptions))
    .map((userEntry) => userEntry.userId);

  return users.length ? { [key]: targetEntry.id, users } : null;
};

const buildSubList = (subInstances = {}) =>
  PRESENCE_KEYS.reduce((subList, key) => {
    const targetMap = subInstances[key] || {};
    const targetEntries = Object.values(targetMap)
      .map((targetEntry) => getSubListEntry(key, targetEntry))
      .filter(Boolean);

    return [...subList, ...targetEntries];
  }, []);

const getSubInstances = (state) => state.subInstances || {};

const updatePresenceState = (state, subInstances) => ({
  ...state,
  subInstances,
  subList: buildSubList(subInstances),
});

const subscribeUser = (state, key, id, userId, subscriptionId) => {
  if (
    !hasValue(id) ||
    !hasValue(userId) ||
    !hasSubscriptionId(subscriptionId)
  ) {
    return state;
  }

  const subInstances = addSubscriptionInstance(
    getSubInstances(state),
    key,
    id,
    userId,
    subscriptionId
  );

  return updatePresenceState(state, subInstances);
};

const unsubscribeUser = (state, key, id, userId, subscriptionId) => {
  if (
    !hasValue(id) ||
    !hasValue(userId) ||
    !hasSubscriptionId(subscriptionId)
  ) {
    return state;
  }

  const subInstances = removeSubscriptionInstance(
    getSubInstances(state),
    key,
    id,
    userId,
    subscriptionId
  );

  return updatePresenceState(state, subInstances);
};

const applyPresenceEvent = (state, key, subData) => {
  if (subData.action === "subscribed") {
    return subscribeUser(
      state,
      key,
      subData.subscribedId,
      subData.userId,
      subData.subscriptionId
    );
  }

  if (subData.action === "unsubscribed") {
    return unsubscribeUser(
      state,
      key,
      subData.subscribedId,
      subData.userId,
      subData.subscriptionId
    );
  }

  return state;
};

const updateSubList = (state, subList) => {
  return {
    ...state,
    subList,
    subInstances: {},
  };
};

// Reducer
export default function reducer(state = initialState, action = {}) {
  switch (action.type) {
    case UPDATE_DOC_SUBS: {
      const subList = Array.isArray(action.subList) ? action.subList : [];

      return updateSubList(state, subList);
    }

    case APPLY_SUB_EVENT: {
      const subData = action.subData;

      if (!subData) return state;

      // DOCUMENT_CHANGED
      if (subData.subscriptionType === "DOCUMENT_CHANGED") {
        return applyPresenceEvent(state, "document", subData);
      }

      // SERVICE_REQUEST_CHANGED
      if (subData.subscriptionType === "SERVICE_REQUEST_CHANGED") {
        return applyPresenceEvent(state, "serviceRequest", subData);
      }

      // USER_SUBSCRIPTIONS
      if (
        subData.subscriptionType === "USER_SUBSCRIPTIONS" &&
        subData.action === "subscribed"
      ) {
        return {
          ...state,
          refreshTime: getRefreshTime(),
        };
      }

      return state;
    }

    case RESUBSCRIBE:
      return {
        ...state,
        refreshTime: action.refreshTime,
      };

    default:
      return state;
  }
}

// Action creators
export const applySubEvent = (subData) => ({
  type: APPLY_SUB_EVENT,
  subData,
});

export const updateDocSubs = (subList) => ({
  type: UPDATE_DOC_SUBS,
  subList,
});
